import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFarmDateTime } from '../../hooks/useFarmDateTime';
import {
  DeviceSyncSeverity,
  DeviceSyncStatusItem,
  formatPendingDuration,
  getSyncSeverity
} from '../../models/device-sync-status';
import { deviceSyncColors } from './colors';

const severityLabelKeys: Record<DeviceSyncSeverity, string> = {
  draining: 'f-device-sync-severity-draining',
  delayed: 'f-device-sync-severity-delayed',
  stuck: 'f-device-sync-severity-stuck'
};

/**
 * The device's errors as the app named them. Keys are the mobile app's vocabulary and the backend does not
 * validate them, so they are printed verbatim - translating them would invent meaning the API never
 * promised.
 */
const ErrorList: React.FC<{ errors: Record<string, number> | null }> = ({ errors }) => {
  const entries = Object.entries(errors ?? {});

  if (entries.length === 0) {
    return <span className="text-gray-300">—</span>;
  }

  return (
    <span className="flex flex-wrap justify-end gap-1">
      {entries.map(([code, count]) => (
        <span
          key={code}
          className="inline-flex items-center gap-1 rounded border border-red-100 bg-red-50 px-1.5 py-0.5 text-[11px] text-red-700"
        >
          <span className="font-mono!">{code}</span>
          <span className="font-semibold tabular-nums">{count}</span>
        </span>
      ))}
    </span>
  );
};

/**
 * One row per device, worst first. The columns are the reader's questions in order: which device, how much
 * is stuck, how long it has been stuck, what went wrong, and when the device last said anything.
 *
 * There is no status column. The severity it showed was derived from the waiting time sitting two columns
 * along, so the two said the same thing twice; the summary line above the table still calls out how many
 * devices are past the stuck threshold.
 */
const DeviceSyncTable: React.FC<{ devices: DeviceSyncStatusItem[] }> = ({ devices }) => {
  const { t } = useTranslation();
  const { formatDate, formatTime } = useFarmDateTime();

  return (
    <div className="overflow-x-auto">
      {/* `border-collapse` by hand: Tailwind runs without preflight here, so cells default to
          border-spacing and the grey header shows a seam between every column. */}
      <table className="w-full border-collapse text-sm">
        <thead className="border-b border-gray-100 bg-gray-50">
          <tr>
            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500">
              {t('f-device-sync-column-device')}
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
              {t('f-device-sync-column-pending')}
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">{t('f-device-sync-column-sent')}</th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
              {t('f-device-sync-column-waiting')}
            </th>
            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">
              {t('f-device-sync-column-errors')}
            </th>
            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">{t('f-com-date')}</th>
            <th className="px-6 py-2 text-left text-xs font-medium text-gray-500">{t('f-com-time')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {devices.map((device) => (
            <tr key={device.macAddress} className="transition-colors hover:bg-gray-50">
              {/* `font-mono!`, not `font-mono`: index.css sets the Roboto face on `*` outside any cascade
                  layer, which outranks every normal utility, so the family only sticks when marked
                  important. A MAC address read off a proportional face is a menace. */}
              <td className="px-6 py-2.5">
                <span className="font-mono! text-xs text-gray-800">{device.macAddress}</span>

                {/* A phone reporting a MAC that never completed a connect is its own problem - the
                    backend keeps the report, so the mismatch has to be visible or it looks like a
                    device that simply is not registered yet. */}
                {!device.injectorMatched && (
                  <span
                    className="ml-2 inline-flex items-center rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-700"
                    title={t('f-device-sync-unmatched-hint')}
                  >
                    {t('f-device-sync-unmatched')}
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5 text-right font-semibold tabular-nums text-gray-900">{device.pendingCount}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">{device.sentCount}</td>
              {/* The dot is what the dropped status column used to spell out. Same three colours, same
                  meanings, but sitting on the number it is a judgement about instead of restating it in a
                  column of its own - and it matches how the stat cards above carry colour. The title is
                  the text equivalent, so the severity is not colour-only. */}
              <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">
                <span className="inline-flex items-center justify-end gap-1.5">
                  <span
                    aria-hidden
                    className="h-1.5 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: deviceSyncColors[getSyncSeverity(device)] }}
                  />
                  <span title={t(severityLabelKeys[getSyncSeverity(device)])}>
                    {formatPendingDuration(device.pendingSince)}
                  </span>
                </span>
              </td>
              <td className="px-4 py-2.5 text-right">
                <ErrorList errors={device.errors} />
              </td>
              {/* Date and time in their own columns, in farm time, the way the medical record table does
                  it. The device's own timestamp, not the receipt time: that is the clock the farm hand
                  was looking at. */}
              <td className="px-4 py-2.5 text-xs text-gray-500">{formatDate(device.reportedAt)}</td>
              <td className="px-6 py-2.5 text-xs text-gray-500">{formatTime(device.reportedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DeviceSyncTable;
